import React from "react";
import * as yup from "yup";
import { Formik } from "formik";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Form, Col, Row } from "react-bootstrap";
import { find } from "lodash";
import { userConstants } from "actions/types";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { getUserState } from "actions/userActions";
import { getIraAccountIdSelector } from "store/selectors/user";
import { formatCurrency, formatAmount } from "utils/number";
import type { RootState } from "types/state";

import Button from "components/Button";
import Alert from "components/Alert";
import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";
import SelectBox from "../../../../signUp/individual/SelectBox";

interface DistributionAmountOption {
  label: string;
  key: string;
  // never present on the literals below; read in JSX, so kept optional
  description?: string;
  items?: React.ReactNode[];
  ordered?: boolean;
}

const distributionAmountOptions: DistributionAmountOption[] = [
  {
    label: "Transfer a specific amount",
    key: "SPECIFIC_AMOUNT",
  },
  {
    label: "Transfer entire account balance",
    key: "ENTIRE_ACCOUNT_BALANCE",
  },
];

const actions = [userConstants.UPDATE_SELECTED_ACCOUNT_TYPE];

const loadingSelector = createLoadingSelector(actions);
// @ts-expect-error - PRESERVED QUIRK: `actions` is already a string[], so
// `[actions]` passes a nested string[][] to createErrorSelector (which expects
// string | string[]). Matches the original JS; do not "fix" the wrapping.
const errorSelector = createErrorSelector([actions]);

const mapStateToProps = (state: RootState) => {
  // userState slice is typed `object`; cast at the boundary to read `.state`
  const userState = (state.user.userState as { state: unknown }).state;
  const accounts = state.accounts.iraAccounts;

  return {
    isSubmitting: loadingSelector(state),
    error: errorSelector(state),
    userState,
    accountId: getIraAccountIdSelector(state),
    accounts,
  };
};

const mapDispatchToProps = {
  getUserState,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  // react-step-wizard injects goToNamedStep at runtime (the workflow omits it);
  // optional in the contract, non-null-asserted at call sites (house pattern).
  goToNamedStep?: (stepName: string) => void;
  selectedAccountType?: string;
  distributionAmount: (payload: {
    distributionAmount: string;
    amount: number | string | null | undefined;
    isTotalDisbursement?: boolean;
  }) => void;
  // react-step-wizard injects stepName at runtime; the workflow declares it
  // on the JSX element.
  stepName?: string;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  distributionAmount: string;
  submitted: boolean;
  amount: number | string;
  showBalanceMessage: boolean;
  // written by _setDistributionAmount but never read
  showInput?: boolean;
}

type AmountValue = number | string | null | undefined;

type SetFieldValueFn = (field: string, value: AmountValue) => void;
type SetFieldTouchedFn = (field: string, touched: boolean) => void;

class DistributionAmount extends React.Component<Props, State> {
  _isMounted = false;

  constructor(props: Props) {
    super(props);

    this.state = {
      distributionAmount: "",
      submitted: false,
      amount: "",
      showBalanceMessage: false,
    };
  }

  _setDistributionAmount = (
    distributionAmount: string,
    setFieldValue: SetFieldValueFn
  ) => {
    const { accounts, selectedAccountType } = this.props;
    const distributionAccount = find(accounts, { id: selectedAccountType });
    // PRESERVED BUG (conversion-only): original dereferences distributionAccount
    // and .balances directly — throws if no matching account or null balances.
    const totalAccountBalance =
      distributionAccount!.balances!.totalAccountBalance;

    const showInput = distributionAmount === "SPECIFIC_AMOUNT";
    const showBalanceMessage = distributionAmount === "ENTIRE_ACCOUNT_BALANCE";

    this.setState({
      distributionAmount,
      showInput,
      showBalanceMessage,
    });

    const amountValue =
      distributionAmount === "ENTIRE_ACCOUNT_BALANCE"
        ? totalAccountBalance
        : this.state.amount;

    const isTotalDisbursement = distributionAmount === "ENTIRE_ACCOUNT_BALANCE";

    setFieldValue("amount", amountValue);

    this.props.distributionAmount({
      distributionAmount,
      amount: amountValue,
      isTotalDisbursement,
    });
  };

  _onAmountChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: SetFieldValueFn,
    setFieldTouched: SetFieldTouchedFn
  ) => {
    const amount = event.target.value;

    const formattedAmount = amount ? formatAmount(amount) : "";

    setFieldValue("amount", formattedAmount);
    setFieldTouched("amount", true);

    this.setState(
      (prevState) => ({
        distributionAmount: prevState.distributionAmount || "SPECIFIC_AMOUNT",
        amount: formattedAmount,
      }),
      () => {
        this.props.distributionAmount({
          distributionAmount: this.state.distributionAmount,
          amount: this.state.amount,
        });
      }
    );
  };

  isButtonDisabled = (
    distributionAmount: string,
    amount: AmountValue,
    errors: { amount?: string }
  ) => {
    const isAmountValid = amount && !errors.amount;
    return !(
      distributionAmount === "ENTIRE_ACCOUNT_BALANCE" ||
      (distributionAmount === "SPECIFIC_AMOUNT" && isAmountValid)
    );
  };

  render() {
    var articleStyle = {
      paddingBottom: 0,
    };

    const { accounts, selectedAccountType } = this.props;
    const distributionAccount = find(accounts, { id: selectedAccountType });

    // PRESERVED BUG (conversion-only): original dereferences distributionAccount
    // and .balances directly — throws if no matching account or null balances.
    // The trailing ! is type-only (erased); a null balance still flows through
    // to formatCurrency exactly as in the original JS.
    const totalAccountBalance =
      distributionAccount!.balances!.totalAccountBalance!;

    const schema = yup.object({
      amount: yup
        .number()
        .nullable()
        .required("Amount is required")
        .label("Specific Amount")
        .test("amount", "Amount must be greater than 0", (value) => value > 0)
        .test(
          "max-amount",
          `Amount cannot exceed available balance of ${formatCurrency(
            totalAccountBalance
          )}`,
          (value) => value <= totalAccountBalance
        ),
    });

    return (
      <div>
        <div className="mega-container">
          <section className="page-title-wrap">
            <article className="text-cell">
              <IconHeader
                variant="pageHeader"
                headerText="How much do you want to withdrawal from your Icon account?"
              />
              <IconSubheader
                variant="subheading"
                subheader="Please choose the amount you'd like to transfer."
              />
            </article>
          </section>

          <Formik
            validateOnChange={true}
            validationSchema={schema}
            onSubmit={(values) => {
              // PRESERVED BUG (conversion-only): this._onSubmit is never defined
              // on the class — submitting throws. Kept verbatim from the JS.
              // @ts-expect-error - _onSubmit does not exist (preserved bug)
              this._onSubmit(values);
            }}
            enableReinitialize={true}
            initialValues={{
              amount: this.state.amount || "",
            }}
            context={{ totalAccountBalance }}
          >
            {({
              setFieldTouched,

              handleSubmit,
              handleBlur,
              setFieldValue,
              values,
              touched,
              errors,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <div>
                  <section
                    className="form-sec-2col"
                    style={{ paddingTop: 20, paddingBottom: 20 }}
                  >
                    <article className="col-form" style={articleStyle}>
                      <div className="account-types">
                        {distributionAmountOptions.map((account) => (
                          <SelectBox
                            label={account.label}
                            description={account.description}
                            key={account.key}
                            items={account.items}
                            ordered={account.ordered}
                            displayChevron={true}
                            onSelect={() => {
                              this._setDistributionAmount(
                                account.key,
                                setFieldValue
                              );
                            }}
                            isSelected={
                              this.state.distributionAmount === account.key
                            }
                          >
                            {this.state.distributionAmount ===
                              "SPECIFIC_AMOUNT" &&
                              account.key === "SPECIFIC_AMOUNT" && (
                                <Row className="mb-3" as={Col} sm={12}>
                                  <Form.Group
                                    className="mb-3"
                                    controlId="formDistributionAmount"
                                  >
                                    <Form.Control
                                      type="number"
                                      name="amount"
                                      placeholder="Enter a dollar amount"
                                      value={values.amount || ""}
                                      onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>
                                      ) =>
                                        this._onAmountChange(
                                          e,
                                          setFieldValue,
                                          setFieldTouched
                                        )
                                      }
                                      onBlur={handleBlur}
                                      isInvalid={
                                        touched.amount && !!errors.amount
                                      }
                                      isValid={touched.amount && !errors.amount}
                                    />

                                    <Form.Control.Feedback type="invalid">
                                      {errors.amount}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                </Row>
                              )}

                            {account.key === "ENTIRE_ACCOUNT_BALANCE" &&
                              this.state.showBalanceMessage && (
                                <Row>
                                  <Col>
                                    <p
                                      style={{
                                        fontSize: 14,
                                        color: "#5e6b75",
                                        margin: "0px 10px 10px",
                                      }}
                                    >
                                      Please note that your final transfer
                                      amount may differ from your current
                                      account balance of{" "}
                                      {formatCurrency(totalAccountBalance)}, as
                                      it depends on market fluctuations and the
                                      price at which your securities are sold.
                                    </p>
                                  </Col>
                                </Row>
                              )}
                          </SelectBox>
                        ))}
                      </div>
                    </article>
                  </section>
                  <section className="form-sec-2col">
                    {this.props.error && (
                      <Alert type="error" msg={this.props.error} />
                    )}
                    <div className="submit-row btn-row">
                      <Button
                        type="button"
                        onClick={() =>
                          this.props.goToNamedStep!("distributionAccountType")
                        }
                        name="cancel"
                        btnLabel="Go Back"
                        color="cancel"
                      />
                      <Button
                        type="button"
                        style={{ marginLeft: 10 }}
                        name="action"
                        disabled={this.isButtonDisabled(
                          this.state.distributionAmount,
                          values.amount,
                          errors
                        )}
                        onClick={() =>
                          this.props.goToNamedStep!("distributionType")
                        }
                      />
                    </div>
                  </section>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    );
  }
}

export default connector(withApollo<Props>(DistributionAmount));
